import { useState } from 'react'
import { Plus, Layers } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { TagBadge } from '@/components/tags/TagBadge'
import { useUpdateVfxShot } from '@/hooks/useVfxShots'
import { useTags } from '@/hooks/useTags'
import type { VfxShot } from '@/types/database'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface VfxShotTagsSectionProps {
  vfxShot: VfxShot
  projectId: string
  canEditTags?: boolean
}

export function VfxShotTagsSection({ vfxShot, projectId, canEditTags = true }: VfxShotTagsSectionProps) {
  const { data: tags } = useTags(projectId)
  const updateVfxShot = useUpdateVfxShot()
  const [isAdding, setIsAdding] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string>('')

  const shotTags = vfxShot.tags || []

  // Get available tags that aren't already applied to this shot
  const availableTags = tags?.filter(
    (tag) => !shotTags.includes(tag.name)
  ) || []

  const handleAddTag = async () => {
    if (!selectedTag) return

    try {
      const newTags = [...shotTags, selectedTag]
      await updateVfxShot.mutateAsync({
        shotId: vfxShot.id,
        updates: {
          tags: newTags,
        },
      })
      setSelectedTag('')
      setIsAdding(false)
    } catch (error) {
      console.error('Failed to add tag:', error)
    }
  }

  const handleRemoveTag = async (tagToRemove: string) => {
    try {
      const newTags = shotTags.filter((tag) => tag !== tagToRemove)
      await updateVfxShot.mutateAsync({
        shotId: vfxShot.id,
        updates: {
          tags: newTags,
        },
      })
    } catch (error) {
      console.error('Failed to remove tag:', error)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Tags ({shotTags.length})
          </CardTitle>
          {canEditTags && !isAdding && availableTags.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Tag List */}
        {shotTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {shotTags.map((tagName) => {
              const tag = tags?.find((t) => t.name === tagName)
              return (
                <TagBadge
                  key={tagName}
                  tag={tag || tagName}
                  color={tag?.color}
                  onRemove={canEditTags ? () => handleRemoveTag(tagName) : undefined}
                  size="md"
                />
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No tags applied</p>
        )}

        {/* Add Tag Form */}
        {isAdding && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select a tag..." />
              </SelectTrigger>
              <SelectContent>
                {availableTags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.name}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span>{tag.display_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleAddTag}
              disabled={!selectedTag || updateVfxShot.isPending}
              className="h-8"
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false)
                setSelectedTag('')
              }}
              className="h-8"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* No tags available message */}
        {isAdding && availableTags.length === 0 && (
          <p className="text-sm text-muted-foreground pt-2 border-t">
            All available tags are already applied. Create new tags in the project settings.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
